import axios, { AxiosResponse } from 'axios'
import FormData from 'form-data'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

const PINATA_API_KEY = 'pinata_api_key'

type PinataPinResponse = {
    IpfsHash: string,
    PinSize: number,
    Timestamp: string
}

export type Web3StorageContextType = {
    applyAPIKey: (apiKey: string) => void
    clearAPIKey: () => void
    apiKey: string | undefined
    upload: (content: string, name: string) => Promise<string>
}

const Web3StorageContext = createContext<Web3StorageContextType>(
    { 
        apiKey: undefined,
        applyAPIKey: (apiKey: string) => {},
        clearAPIKey: () => {},
        upload: async (content: string, name: string) => ('')
    })

export default function Web3StorageProvider(props: { children: ReactNode }) {
    const [apiKey, setAPIKey] = useState<string | undefined>()

    const apply = (apiKey: string) => {
        localStorage.setItem(PINATA_API_KEY, apiKey)
        setAPIKey(apiKey)
    }

    const clear = () => {
        localStorage.removeItem(PINATA_API_KEY)
        setAPIKey(undefined)
    }

    const load = () => {
        const apiKey = localStorage.getItem(PINATA_API_KEY)
        if (apiKey) {
            setAPIKey(apiKey)
        } else {
            console.log('PinataStorageProvider:', 'no API key in storage')
        }
    }

    const upload = async (content: string, name: string): Promise<string> => {
        const formData = new FormData()
        formData.append('file', new Blob([content]))

        const pinataMetadata = JSON.stringify({
            name,
        })
        formData.append('pinataMetadata', pinataMetadata)
          
        const pinataOptions = JSON.stringify({
            cidVersion: 0,
        })
        formData.append('pinataOptions', pinataOptions)

        const res = await axios.post<any, AxiosResponse<PinataPinResponse>>('https://api.pinata.cloud/pinning/pinFileToIPFS', formData, {
            headers: {
                'Authorization': `Bearer ${apiKey}`,
            }
        })
        return res.data.IpfsHash
    }

    useEffect(() => {
        load()
    }, [])

    const value = {
        apiKey,
        applyAPIKey: apply,
        clearAPIKey: clear,
        upload
    }

    return (
        <Web3StorageContext.Provider value={value}>
            {props.children}
        </Web3StorageContext.Provider>
    )
}

export const useWeb3Storage = () => {
    return useContext(Web3StorageContext)
}